import { useState } from "react";

import { CheckCircleRounded, ErrorOutlineRounded, ExpandMoreRounded, StorageRounded } from "@mui/icons-material";
import { Box, ButtonBase, CircularProgress, Collapse, Stack, Typography } from "@mui/material";

import { ResultTable } from "./ResultTable";

const TOOL_LABELS = {
	execute_sql_readonly: "Queried the database",
};

// One-line summary of a query: the tables it reads, e.g. "products_alllist, products_product +2".
function describeInput(input) {
	const sql = input?.sqlStatement;
	if (typeof sql !== "string") {
		return null;
	}
	const tables = [...new Set([...sql.matchAll(/\b(?:from|join)\s+([a-z_][a-z0-9_]*)/gi)].map((m) => m[1]))].filter(
		(table) => table.includes("_"),
	);
	if (tables.length === 0) {
		return null;
	}
	return tables.length > 2 ? `${tables.slice(0, 2).join(", ")} +${tables.length - 2}` : tables.join(", ");
}

export function ToolActivity({ part }) {
	const [open, setOpen] = useState(false);
	const { name, status, input, result } = part;
	const running = status === "pending" || status === "running";
	const hasDetail = Boolean(input?.sqlStatement || result?.columns || result?.error);
	const detail = describeInput(input);
	const sql = result?.sql || input?.sqlStatement;

	const icon = running ? (
		<CircularProgress size={14} thickness={5} />
	) : status === "error" ? (
		<ErrorOutlineRounded sx={{ fontSize: 16, color: "warning.dark" }} />
	) : (
		<CheckCircleRounded sx={{ fontSize: 16, color: "success.main" }} />
	);

	return (
		<Box sx={{ my: 0.75, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}>
			<ButtonBase
				onClick={() => hasDetail && setOpen((v) => !v)}
				disabled={!hasDetail}
				aria-expanded={open}
				sx={{ width: "100%", justifyContent: "flex-start", px: 1.5, py: 0.75, borderRadius: 1, textAlign: "left" }}
			>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
					<StorageRounded sx={{ fontSize: 16, color: "text.secondary" }} />
					<Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
						{TOOL_LABELS[name] ?? name}
					</Typography>
					{detail && (
						<Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{detail}
						</Typography>
					)}
					<Box sx={{ flexGrow: 1 }} />
					{result?.row_count !== undefined && (
						<Typography variant="smallText" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
							{result.row_count}
							{result.truncated ? "+" : ""} rows{result.elapsed_ms !== undefined ? ` · ${(result.elapsed_ms / 1000).toFixed(1)}s` : ""}
						</Typography>
					)}
					{icon}
					{hasDetail && <ExpandMoreRounded sx={{ fontSize: 18, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
				</Stack>
			</ButtonBase>
			<Collapse in={open} unmountOnExit>
				<Box sx={{ px: 1.5, pb: 1.5 }}>
					{result?.error && (
						<Typography variant="body2" sx={{ color: "warning.dark", mb: 1 }}>
							{result.error}
						</Typography>
					)}
					{sql && (
						<Box
							component="pre"
							sx={{ m: 0, mb: 1, p: 1.25, bgcolor: "#0f172a", color: "#e2e8f0", borderRadius: 1, fontSize: 12, overflowX: "auto", whiteSpace: "pre-wrap" }}
						>
							{sql}
						</Box>
					)}
					{result?.columns && result.rows?.length > 0 && <ResultTable columns={result.columns} rows={result.rows} maxHeight={260} />}
				</Box>
			</Collapse>
		</Box>
	);
}
